import React, { useState, useEffect } from 'react';
import { Artist, Project, ProfileType } from '../types';
import { SPECIALIZATIONS, SOFTWARE, ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';
import Modal from './ui/Modal';
import Input from './ui/Input';
import Button from './ui/Button';
import Select from './ui/Select';
import Textarea from './ui/Textarea';
import AvailabilityCalendar from './AvailabilityCalendar';
import ProfileAccessSection from './ProfileAccessSection';
import { getAvatarUrl } from '../utils/avatarUtils';
import { API_CONFIG } from '../config';
import { apiFetch, apiPost } from '../utils/api';

const STAFF_TYPES = [
  ProfileType.PRODUCER,
  ProfileType.SUPERVISOR,
  ProfileType.COORDINATOR,
  ProfileType.SALES,
  ProfileType.FINDER,
  ProfileType.ADMIN,
] as const;

type WizardStep = 'type' | 'fields' | 'access';

interface ProfileWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (artist: Artist, avatarFile?: File) => void;
  onProfileCreated?: (profile: Artist) => void;
  projects: Project[];
}

const ProfileWizardModal: React.FC<ProfileWizardModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onProfileCreated,
  projects,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<WizardStep>('type');
  const [category, setCategory] = useState<'artist' | 'staff' | 'client' | null>(null);
  const [profileType, setProfileType] = useState<string>(ProfileType.VFX_ARTIST);
  const [savedProfile, setSavedProfile] = useState<Artist | null>(null);

  const [form, setForm] = useState({
    name: '',
    email: '',
    telegram: '',
    organization: '',
    specialization: SPECIALIZATIONS[0],
    software: [] as string[],
    availability: [] as string[],
    portfolioUrl: '',
    description: '',
    location: '',
    assignedProjects: [] as string[],
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep('type');
    setCategory(null);
    setProfileType(ProfileType.VFX_ARTIST);
    setSavedProfile(null);
    setForm({
      name: '',
      email: '',
      telegram: '',
      organization: '',
      specialization: SPECIALIZATIONS[0],
      software: [],
      availability: [],
      portfolioUrl: '',
      description: '',
      location: '',
      assignedProjects: [],
    });
    setAvatarFile(null);
    setAvatarPreview(null);
  }, [isOpen]);

  const isArtist = profileType === ProfileType.VFX_ARTIST;

  const handleCategorySelect = (cat: 'artist' | 'staff' | 'client', type?: string) => {
    setCategory(cat);
    if (cat === 'artist') {
      setProfileType(ProfileType.VFX_ARTIST);
      setStep('fields');
    } else if (cat === 'client') {
      setProfileType(ProfileType.CLIENT);
      setStep('fields');
    } else if (type) {
      setProfileType(type);
      setStep('fields');
    }
  };

  const buildArtistPayload = (): Artist => ({
    id: '',
    name: form.name,
    email: form.email,
    telegram: form.telegram,
    organization: form.organization || null,
    specialization: isArtist ? form.specialization : '',
    software: isArtist ? form.software : [],
    availability: isArtist ? form.availability : [],
    portfolioUrl: form.portfolioUrl,
    description: form.description,
    location: form.location,
    assignedProjects: form.assignedProjects,
    profileType,
    avatarUrl: avatarPreview || undefined,
  });

  const handleSaveFields = async () => {
    const artist = buildArtistPayload();
    setSaveError('');

    if (isArtist) {
      onSave(artist, avatarFile || undefined);
      onClose();
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        telegram: form.telegram,
        organization: form.organization,
        profileType,
        specialization: '',
        software: [],
        availability: [],
        assignedProjects: [],
      };

      let response: Response;
      if (avatarFile) {
        const formData = new FormData();
        formData.append('avatar', avatarFile);
        Object.entries(payload).forEach(([key, val]) => {
          if (Array.isArray(val)) formData.append(key, JSON.stringify(val));
          else if (val != null) formData.append(key, String(val));
        });
        response = await apiFetch(API_CONFIG.ARTISTS.CREATE, { method: 'POST', body: formData });
      } else {
        response = await apiPost(API_CONFIG.ARTISTS.CREATE, payload);
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create profile');

      setSavedProfile(data);
      onProfileCreated?.(data);
      setStep('access');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const title =
    step === 'type'
      ? t('profileWizard.selectType')
      : step === 'fields'
        ? t('profileWizard.profileFields')
        : t('profileWizard.accessStep');

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="lg">
      {step === 'type' && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => handleCategorySelect('artist')}
            className="w-full bg-gray-700/50 hover:bg-gray-700 border border-gray-600 hover:border-indigo-500 rounded-lg p-4 text-left"
          >
            <h3 className="text-white font-semibold">{t('register.types.vfxArtist')}</h3>
            <p className="text-sm text-gray-400">{t('register.types.vfxArtistDesc')}</p>
          </button>

          <button
            type="button"
            onClick={() => handleCategorySelect('client')}
            className="w-full bg-gray-700/50 hover:bg-gray-700 border border-gray-600 hover:border-indigo-500 rounded-lg p-4 text-left"
          >
            <h3 className="text-white font-semibold">{t('register.types.client')}</h3>
          </button>

          <div>
            <h3 className="text-white font-semibold mb-2">{t('register.types.staffGroup')}</h3>
            <div className="grid grid-cols-2 gap-2">
              {STAFF_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleCategorySelect('staff', type)}
                  className="bg-gray-700/50 hover:bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white text-left"
                >
                  {t(`register.types.${type}`)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 'fields' && (
        <div className="space-y-4">
          <Input
            label={t('artistForm.name')}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <Input
            label={t('artistForm.email')}
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            required
          />
          <Input
            label={t('artistForm.telegram')}
            value={form.telegram}
            onChange={(e) => setForm((f) => ({ ...f, telegram: e.target.value }))}
          />

          {profileType === ProfileType.CLIENT && (
            <Input
              label={t('register.organization')}
              value={form.organization}
              onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))}
            />
          )}

          {isArtist && (
            <>
              <Select
                label={t('artistForm.specialization')}
                value={form.specialization}
                onChange={(e) => setForm((f) => ({ ...f, specialization: e.target.value }))}
              >
                {SPECIALIZATIONS.map((spec) => (
                  <option key={spec} value={spec}>{spec}</option>
                ))}
              </Select>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('artistForm.software')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {SOFTWARE.map((sw) => (
                    <label key={sw} className="flex items-center gap-1 text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={form.software.includes(sw)}
                        onChange={() =>
                          setForm((f) => ({
                            ...f,
                            software: f.software.includes(sw)
                              ? f.software.filter((s) => s !== sw)
                              : [...f.software, sw],
                          }))
                        }
                      />
                      {sw}
                    </label>
                  ))}
                </div>
              </div>

              <Input
                label={t('artistForm.portfolioUrl')}
                value={form.portfolioUrl}
                onChange={(e) => setForm((f) => ({ ...f, portfolioUrl: e.target.value }))}
              />

              <Textarea
                label={t('artistForm.description')}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />

              <AvailabilityCalendar
                selectedDates={form.availability}
                onDatesChange={(dates) => setForm((f) => ({ ...f, availability: dates }))}
              />

              <div>
                <label className="block text-sm text-gray-300 mb-1">{t('artistForm.avatar')}</label>
                {avatarPreview && (
                  <img
                    src={getAvatarUrl(avatarPreview) || avatarPreview}
                    alt=""
                    className="w-16 h-16 rounded-full mb-2 object-cover"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setAvatarFile(file);
                      const reader = new FileReader();
                      reader.onloadend = () => setAvatarPreview(reader.result as string);
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="text-sm text-gray-400"
                />
              </div>
            </>
          )}

          {saveError && <p className="text-red-400 text-sm">{saveError}</p>}

          <div className="flex gap-2 pt-2">
            <Button variant="secondary" onClick={() => setStep('type')}>
              {t('artistForm.previous')}
            </Button>
            {!isArtist ? (
              <Button onClick={handleSaveFields} disabled={saving} className="flex-1 justify-center">
                {saving ? '...' : t('artistForm.next')}
              </Button>
            ) : (
              <Button onClick={handleSaveFields} className="flex-1 justify-center">
                {t('artistForm.save')}
              </Button>
            )}
          </div>
        </div>
      )}

      {step === 'access' && savedProfile && (
        <div>
          <p className="text-gray-400 text-sm mb-4">{t('profileWizard.accessHint')}</p>
          <ProfileAccessSection
            profile={savedProfile}
            onUpdated={(p) => {
              setSavedProfile(p);
              if (p.id && p.id !== 'pending') {
                onClose();
              }
            }}
          />
          <div className="mt-4 flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              {t('artistForm.cancel')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ProfileWizardModal;
